import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { handleCall, handleEmail, GMAIL_COMPOSE_URL } from "../lib/contact";

const MOBILE_MENU_ICON_SIZE = 22;

const TOAST_OPTIONS = {
    style: {
        background: "#FF1A1A",
        color: "#000000",
        border: "2px solid #000000",
        borderRadius: "0",
        fontFamily: "'Outfit', sans-serif",
        fontWeight: "600",
    },
};

const navLinkClass = ({ isActive }) =>
    `font-heading text-lg uppercase tracking-widest transition-colors duration-200 ${
        isActive ? "text-[#FF1A1A]" : "text-white hover:text-[#FF1A1A]"
    }`;

const DesktopNav = () => (
    <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
        <NavLink to="/" end className={navLinkClass} data-testid="nav-home">
            Home
        </NavLink>
        <NavLink to="/about" className={navLinkClass} data-testid="nav-about">
            About
        </NavLink>
        <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">
            Contact
        </NavLink>
    </nav>
);

const MobileNav = () => (
    <div
        className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
        data-testid="mobile-nav"
    >
        <nav className="flex flex-col px-6 py-6 gap-6">
            <NavLink to="/" end className={navLinkClass} data-testid="mobile-nav-home">
                Home
            </NavLink>
            <NavLink to="/about" className={navLinkClass} data-testid="mobile-nav-about">
                About
            </NavLink>
            <NavLink to="/contact" className={navLinkClass} data-testid="mobile-nav-contact">
                Contact
            </NavLink>
        </nav>
    </div>
);

const Header = ({ mobileOpen, onToggleMobile }) => (
    <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
        data-testid="site-header"
    >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
            <Link
                to="/"
                className="font-heading text-2xl md:text-3xl text-[#FF1A1A] uppercase tracking-wider leading-none"
                data-testid="logo-link"
            >
                Elijah<span className="text-white">.</span>Golden
            </Link>
            <DesktopNav />
            <button
                className="md:hidden text-white p-2 border border-white/20 hover:border-[#FF1A1A] transition-colors"
                onClick={onToggleMobile}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
            >
                {mobileOpen ? (
                    <X size={MOBILE_MENU_ICON_SIZE} />
                ) : (
                    <Menu size={MOBILE_MENU_ICON_SIZE} />
                )}
            </button>
        </div>
        {mobileOpen && <MobileNav />}
    </header>
);

const Footer = () => (
    <footer
        className="border-t border-white/10 bg-[#050505] py-12 px-6 md:px-12"
        data-testid="site-footer"
    >
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
                <p className="font-heading text-2xl text-white uppercase tracking-wider">
                    Elijah <span className="text-[#FF1A1A]">Golden</span>
                </p>
                <p className="text-zinc-500 text-sm mt-2 max-w-md">
                    Whatever you desire, I will help you achieve it.
                </p>
            </div>
            <div className="flex flex-col md:items-end gap-1 text-sm">
                <a
                    href={GMAIL_COMPOSE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleEmail}
                    className="text-zinc-400 hover:text-[#FF1A1A] transition-colors cursor-pointer"
                    data-testid="footer-email"
                >
                    tellmeyourdesire666@gmail.com
                </a>
                <a
                    href="tel:+14073744236"
                    onClick={handleCall}
                    className="text-zinc-400 hover:text-[#FF1A1A] transition-colors cursor-pointer"
                    data-testid="footer-phone"
                >
                    407-374-4236
                </a>
            </div>
        </div>
        <div className="max-w-[1600px] mx-auto mt-8 pt-6 border-t border-white/5 text-xs text-zinc-600 uppercase tracking-widest">
            © {new Date().getFullYear()} Elijah Golden · All Rights Reserved
        </div>
    </footer>
);

const Layout = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setMobileOpen(false);
        window.scrollTo(0, 0);
    }, [location.pathname, setMobileOpen]);

    return (
        <div className="min-h-screen flex flex-col bg-[#050505]" data-testid="layout-root">
            <Header mobileOpen={mobileOpen} onToggleMobile={() => setMobileOpen((v) => !v)} />
            <main className="flex-1" data-testid="main-content">
                <Outlet />
            </main>
            <Footer />
            <Toaster position="bottom-right" richColors toastOptions={TOAST_OPTIONS} />
        </div>
    );
};

export default Layout;
